import { FormEvent, useState } from "react"
import { Head, router } from "@inertiajs/react"
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControl,
  FormControlLabel, InputLabel, MenuItem, Paper, Select, Stack, Step,
  StepLabel, Stepper, TextField, Typography
} from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"
import AudioPlayer from "../../../components/AudioPlayer"
import TranslationsEditor, { TranslationDraft } from "../../../components/TranslationsEditor"

type WordTranslation = {
  language: "en" | "de"
  text: string
  hint: string
}

type AudioMeta = {
  signed_id: string
  url: string
  language: string
  ai_model: string
  voice: string
  instructions: string | null
}

type ExtractedWord = {
  kanji: string
  kana: string
  romaji: string
  romaji_obvious: boolean
  part_of_speech: string
  translations: WordTranslation[]
  existing_id: number | null
  existing_audio: boolean
  existing_translations: WordTranslation[]
  // local UI fields
  include?: boolean
  audio?: AudioMeta | null
}

type Draft = {
  kanji: string
  kana: string
  romaji: string
  translations: TranslationDraft[]
  words: ExtractedWord[]
}

const steps = ["English", "Review translation", "Audio", "Words", "Save"]

function csrf() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
}

async function postJson<T>(url: string, body: any): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrf() },
    credentials: "same-origin",
    body: JSON.stringify(body)
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || `Request failed: ${res.status}`)
  }
  return res.json() as Promise<T>
}

// Append translations as `prefix[translations][i][...]` (the AI controller's
// expected shape — distinct from `translations_attributes` used by the regular
// nested-attributes endpoints).
function appendAiTranslations(
  data: FormData, prefix: string, translations: { language: string; text: string; hint?: string; literal?: string; position?: number }[]
) {
  translations.forEach((t, i) => {
    const text = (t.text ?? "").trim()
    if (!text) return
    const base = `${prefix}[translations][${i}]`
    data.append(`${base}[language]`, t.language)
    data.append(`${base}[text]`, text)
    data.append(`${base}[hint]`, t.hint ?? "")
    if (t.literal !== undefined) data.append(`${base}[literal]`, t.literal ?? "")
    data.append(`${base}[position]`, String(t.position ?? i))
  })
}

function normalizeDraftTranslations(raw: WordTranslation[] | undefined): TranslationDraft[] {
  return (raw ?? []).map((t) => ({
    language: t.language,
    text: t.text,
    hint: t.hint ?? "",
    literal: ""
  }))
}

function normalizeWordTranslations(raw: WordTranslation[] | undefined): TranslationDraft[] {
  return (raw ?? []).map((t) => ({
    language: t.language,
    text: t.text,
    hint: t.hint ?? ""
  }))
}

export default function NewSentenceAi({ topics }: { topics: { id: number; name: string }[] }) {
  const [step, setStep] = useState(0)
  const [english, setEnglish] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [sentenceAudio, setSentenceAudio] = useState<AudioMeta | null>(null)
  const [topicIds, setTopicIds] = useState<number[]>([])

  const onDraft = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true); setError(null)
    try {
      const data = await postJson<{ draft: any }>("/admin/ai/sentence/draft", { english })
      const words: ExtractedWord[] = (data.draft.words ?? []).map((w: any) => ({
        kanji: w.kanji ?? "",
        kana: w.kana ?? "",
        romaji: w.romaji ?? "",
        romaji_obvious: !!w.romaji_obvious,
        part_of_speech: w.part_of_speech ?? "",
        translations: normalizeWordTranslations(w.translations) as WordTranslation[],
        existing_id: w.existing_id ?? null,
        existing_audio: !!w.existing_audio,
        existing_translations: (w.existing_translations ?? []) as WordTranslation[],
        include: !w.existing_id
      }))
      setDraft({
        kanji: data.draft.kanji ?? "",
        kana: data.draft.kana ?? "",
        romaji: data.draft.romaji ?? "",
        translations: normalizeDraftTranslations(data.draft.translations),
        words
      })
      setStep(1)
    } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  const generateSentenceAudio = async () => {
    if (!draft) return
    setLoading(true); setError(null)
    try {
      const data = await postJson<{ audio: AudioMeta }>(
        "/admin/ai/sentence/audio", { text: draft.kanji || draft.kana }
      )
      setSentenceAudio(data.audio)
    } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  const generateWordAudio = async (index: number) => {
    if (!draft) return
    const w = draft.words[index]
    setLoading(true); setError(null)
    try {
      const data = await postJson<{ audio: AudioMeta }>(
        "/admin/ai/sentence/audio", { text: w.kana || w.kanji }
      )
      const updated = [...draft.words]
      updated[index] = { ...w, audio: data.audio }
      setDraft({ ...draft, words: updated })
    } catch (e: any) { setError(e.message) } finally { setLoading(false) }
  }

  const appendAudioMeta = (data: FormData, prefix: string, audio: AudioMeta) => {
    data.append(`${prefix}[audio_signed_id]`, audio.signed_id)
    data.append(`${prefix}[audio_language]`, audio.language)
    data.append(`${prefix}[audio_ai_model]`, audio.ai_model)
    data.append(`${prefix}[audio_voice]`, audio.voice)
    if (audio.instructions) data.append(`${prefix}[audio_instructions]`, audio.instructions)
  }

  const toggleWord = (i: number) => {
    if (!draft) return
    const w = draft.words[i]
    const updated = [...draft.words]
    updated[i] = { ...w, include: !w.include }
    setDraft({ ...draft, words: updated })
  }

  const updateDraftField = (key: "kanji" | "kana" | "romaji", value: string) => {
    if (!draft) return
    setDraft({ ...draft, [key]: value })
  }

  const updateDraftTranslations = (next: TranslationDraft[]) => {
    if (!draft) return
    setDraft({ ...draft, translations: next })
  }

  const updateWord = (i: number, patch: Partial<ExtractedWord>) => {
    if (!draft) return
    const updated = [...draft.words]
    updated[i] = { ...updated[i], ...patch }
    setDraft({ ...draft, words: updated })
  }

  const updateWordTranslations = (i: number, next: TranslationDraft[]) => {
    if (!draft) return
    const updated = [...draft.words]
    const trimmed: WordTranslation[] = next
      .filter((t) => !t._destroy)
      .map((t) => ({ language: t.language, text: t.text, hint: t.hint ?? "" }))
    updated[i] = { ...updated[i], translations: trimmed }
    setDraft({ ...draft, words: updated })
  }

  const onSave = () => {
    if (!draft) return
    const data = new FormData()
    data.append("sentence[kanji]", draft.kanji)
    data.append("sentence[kana]", draft.kana)
    data.append("sentence[romaji]", draft.romaji)
    if (sentenceAudio) appendAudioMeta(data, "sentence", sentenceAudio)
    appendAiTranslations(data, "sentence", draft.translations)

    const include = draft.words.filter((w) => w.include || w.existing_id)
    include.forEach((w, i) => {
      if (w.existing_id) {
        data.append(`words[${i}][id]`, String(w.existing_id))
      } else {
        data.append(`words[${i}][kanji]`, w.kanji)
        data.append(`words[${i}][kana]`, w.kana)
        data.append(`words[${i}][romaji]`, w.romaji)
        data.append(`words[${i}][romaji_obvious]`, String(w.romaji_obvious))
        if (w.audio) appendAudioMeta(data, `words[${i}]`, w.audio)
      }
      appendAiTranslations(data, `words[${i}]`, w.translations)
    })
    topicIds.forEach((id) => data.append("topic_ids[]", String(id)))
    if (topicIds.length === 0) data.append("topic_ids[]", "")
    router.post("/admin/ai/sentence", data, { forceFormData: true })
  }

  return (
    <AdminLayout title="New sentence (AI)">
      <Head title="New sentence (AI)" />
      <Stepper activeStep={step} sx={{ mb: 3 }} data-testid="ai-stepper">
        {steps.map((s) => (<Step key={s}><StepLabel>{s}</StepLabel></Step>))}
      </Stepper>
      {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="ai-error">{error}</Alert>}
      {loading && <Box sx={{ mb: 2 }}><CircularProgress size={24} /> Working…</Box>}

      {step === 0 && (
        <Paper component="form" onSubmit={onDraft} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Enter an English sentence</Typography>
          <TextField
            fullWidth multiline rows={3} required value={english}
            onChange={(e) => setEnglish(e.target.value)}
            label="English sentence"
            slotProps={{ htmlInput: { "data-testid": "ai-english" } }}
          />
          <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading || !english.trim()}
                  data-testid="ai-translate">
            Translate with AI
          </Button>
        </Paper>
      )}

      {step >= 1 && draft && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6">Translation</Typography>
          <Stack direction={{ xs: "column", md: "row" }} gap={2} sx={{ mt: 2 }}>
            <TextField label="Kanji" value={draft.kanji} fullWidth
                       onChange={(e) => updateDraftField("kanji", e.target.value)}
                       slotProps={{ htmlInput: { "data-testid": "ai-kanji" } }} />
            <TextField label="Kana" value={draft.kana} fullWidth
                       onChange={(e) => updateDraftField("kana", e.target.value)}
                       slotProps={{ htmlInput: { "data-testid": "ai-kana" } }} />
          </Stack>
          <TextField label="Romaji" value={draft.romaji} fullWidth sx={{ mt: 2 }}
                     onChange={(e) => updateDraftField("romaji", e.target.value)} />
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Translations</Typography>
            <TranslationsEditor
              translations={draft.translations}
              onChange={updateDraftTranslations}
              withLiteral
              testIdPrefix="ai-sentence-translation"
            />
          </Box>
          {step === 1 && (
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => setStep(2)} data-testid="ai-step-audio">
              Continue to audio
            </Button>
          )}
        </Paper>
      )}

      {step >= 2 && draft && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6">Sentence audio</Typography>
          <Box sx={{ mt: 2 }}>
            <AudioPlayer src={sentenceAudio?.url} />
          </Box>
          <Stack direction="row" gap={2} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={generateSentenceAudio} disabled={loading} data-testid="ai-gen-sentence-audio">
              {sentenceAudio ? "Regenerate audio" : "Generate audio"}
            </Button>
            {step === 2 && (
              <Button variant="contained" onClick={() => setStep(3)} data-testid="ai-step-words">
                Continue to words
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {step >= 3 && draft && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6">Words ({draft.words.length})</Typography>
          <Stack gap={2} sx={{ mt: 2 }}>
            {draft.words.map((w, i) => (
              <Box key={i} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                   data-testid={`ai-word-${i}`}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">
                    {w.kanji || w.kana} <Chip size="small" label={w.part_of_speech} sx={{ ml: 1 }} />
                    {w.existing_id && <Chip size="small" color="success" label="Already in DB" sx={{ ml: 1 }} />}
                  </Typography>
                  {!w.existing_id && (
                    <FormControlLabel
                      control={<Checkbox checked={!!w.include} onChange={() => toggleWord(i)}
                                         data-testid={`ai-word-include-${i}`} />}
                      label="Include & create" />
                  )}
                </Stack>
                {w.existing_id && w.existing_translations.length > 0 && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">Existing translations</Typography>
                    {w.existing_translations.map((t, ti) => (
                      <Typography key={ti} variant="body2">
                        [{t.language}] {t.text}{t.hint ? ` — ${t.hint}` : ""}
                      </Typography>
                    ))}
                  </Box>
                )}
                {(!w.existing_id && w.include) || w.existing_id ? (
                  <Stack gap={1} sx={{ mt: 1 }}>
                    {!w.existing_id && (
                      <>
                        <Stack direction={{ xs: "column", md: "row" }} gap={1}>
                          <TextField label="Kanji" size="small" value={w.kanji}
                                     onChange={(e) => updateWord(i, { kanji: e.target.value })} />
                          <TextField label="Kana" size="small" value={w.kana}
                                     onChange={(e) => updateWord(i, { kana: e.target.value })} />
                          <TextField label="Romaji" size="small" value={w.romaji}
                                     onChange={(e) => updateWord(i, { romaji: e.target.value })} />
                        </Stack>
                        <Stack direction="row" gap={2} alignItems="center">
                          <Button size="small" variant="outlined" onClick={() => generateWordAudio(i)} disabled={loading}
                                  data-testid={`ai-word-audio-${i}`}>
                            {w.audio ? "Regenerate audio" : "Generate audio"}
                          </Button>
                          <AudioPlayer src={w.audio?.url ?? null} />
                        </Stack>
                      </>
                    )}
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      {w.existing_id ? "Additional translations to add (duplicates ignored)" : "Translations"}
                    </Typography>
                    <TranslationsEditor
                      translations={w.translations.map((t) => ({
                        language: t.language,
                        text: t.text,
                        hint: t.hint ?? ""
                      }))}
                      onChange={(next) => updateWordTranslations(i, next)}
                      withHint={false}
                      testIdPrefix={`ai-word-${i}-translation`}
                    />
                  </Stack>
                ) : null}
              </Box>
            ))}
          </Stack>
          {step === 3 && (
            <Button variant="contained" sx={{ mt: 2 }} onClick={() => setStep(4)} data-testid="ai-step-save">
              Continue to save
            </Button>
          )}
        </Paper>
      )}

      {step === 4 && draft && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6">Save</Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="ai-topics-label">Topics (optional)</InputLabel>
            <Select labelId="ai-topics-label" multiple value={topicIds} label="Topics (optional)"
                    onChange={(e) => setTopicIds(e.target.value as number[])}
                    renderValue={(selected) => topics.filter((t) => selected.includes(t.id)).map((t) => t.name).join(", ")}>
              {topics.map((t) => (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>))}
            </Select>
          </FormControl>
          <Stack direction="row" gap={2} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={onSave} data-testid="ai-save">Save sentence</Button>
            <Button onClick={() => setStep(0)}>Start over</Button>
          </Stack>
        </Paper>
      )}
    </AdminLayout>
  )
}
